import React from 'react';
import { usePresentationMode } from '@/contexts/PresentationModeContext';

/**
 * useChartMask - Provides masked formatting for Recharts components
 * 
 * Shows approximate values (e.g., ~156K) instead of exact numbers
 * Charts remain visible, only numeric precision is reduced.
 */
export const useChartMask = () => {
  const { isPresentationMode, maskNumber } = usePresentationMode();

  // Mask axis tick values - show approximate
  const maskAxisTick = (value) => {
    if (!isPresentationMode) {
      if (typeof value === 'number') {
        if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
        if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
        return value.toLocaleString();
      }
      return value;
    }
    // In presentation mode, show approximate
    if (typeof value === 'number') {
      if (value >= 1000000) return `~${Math.round(value / 100000) / 10}M`;
      if (value >= 1000) return `~${Math.round(value / 1000)}K`;
      if (value >= 100) return `~${Math.round(value / 10) * 10}`;
      return `~${Math.round(value / 5) * 5}`;
    }
    return value;
  };

  // Mask tooltip values - show approximate
  const maskTooltip = (value, name) => {
    if (!isPresentationMode) {
      if (typeof value === 'number') return [value.toLocaleString(), name];
      return [value, name];
    }
    // In presentation mode, show approximate
    return [maskNumber(value), name];
  };

  // Mask tooltip for currency
  const maskCurrencyTooltip = (value, name) => {
    if (!isPresentationMode) {
      if (typeof value === 'number') return [`$${value.toLocaleString()}`, name];
      return [value, name];
    }
    // In presentation mode
    if (typeof value === 'number') {
      if (value >= 1000000) return [`~$${Math.round(value / 100000) / 10}M`, name];
      if (value >= 1000) return [`~$${Math.round(value / 1000)}K`, name];
      return [`~$${Math.round(value / 10) * 10}`, name];
    }
    return [value, name];
  };

  // Whether to show exact data labels on bars/lines
  const showLabels = !isPresentationMode;

  return {
    isPresentationMode,
    maskAxisTick,
    maskTooltip,
    maskCurrencyTooltip,
    showLabels,
  };
};

export default useChartMask;
